import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  CssBaseline,
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import MiniDrawer from "./MiniDrawer";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { trTR } from "@mui/x-data-grid/locales";
import { useIntl, IntlProvider, FormattedMessage } from "react-intl";
import messages from "../../locales";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../Themes/themes";
import isBetween from "dayjs/plugin/isBetween";
import { FLOAT } from "html2canvas/dist/types/css/property-descriptors/float";
import StudentInfoModal from "./StudentInfoModal";
import * as XLSX from "xlsx";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
dayjs.extend(isBetween);
//Öğrenci listesi sayfası
const SchoolReportList: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<{
    student_id: number;
    student_name: string;
    session_class: string;
    session_branch: string;
    school_name: string;
    dyslexia_possibility: number;
    session_type: number;
    tc: string;
    province: string;
    district: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [schoolName, setSchoolName] = useState<string>("");
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSchool, setLoadingSchool] = useState(true);
  const location = useLocation();
  const initialSchoolId = location.state?.schoolId;
  const language = location.state?.language;
  //const academicYears = useMemo(() => generateAcademicYears(), []);
  const [years, setYears] = useState([]);
  const [noData, setNoData] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [academicYears, setAcademicYears] = useState<string[]>([]);
  useEffect(() => {
    if (language) {
      localStorage.setItem("selectedLanguage", language);
    }
  }, [language]);
  const [schoolId, setSchoolId] = useState(initialSchoolId);
  const intl = useIntl();
  useEffect(() => {
    if (initialSchoolId) {
      setSchoolId(initialSchoolId);
      localStorage.setItem("schoolId", initialSchoolId.toString());
    }
  }, [initialSchoolId]);
  const getDyslexiaLevelText = (value: number | null | undefined): string => {
    if (value == null) return "-";

    const percent = Math.round(value * 100);

    if (percent >= 0 && percent < 25)
      return intl.formatMessage({ id: "very_low" });

    if (percent >= 25 && percent < 50) return intl.formatMessage({ id: "low" });

    if (percent >= 50 && percent < 75)
      return intl.formatMessage({ id: "high" });

    if (percent >= 75) return intl.formatMessage({ id: "very_high" });

    return "-";
  };
  useEffect(() => {
    const fetchSchoolData = async () => {
      setLoadingSchool(true);
      if (schoolId === 9999) {
        const defaultSchoolName = intl.formatMessage({ id: "meb" });
        setSchoolName(defaultSchoolName);
        localStorage.setItem("schoolName", defaultSchoolName); // Save default schoolName to localStorage
        setLoadingSchool(false);
      } else {
        try {
          const response = await axios.get(
            `https://eyelexia.eyesoft.com.tr/api/get-school?id=${schoolId}`
          );
          setSchoolName(response.data.Name);
          localStorage.setItem("schoolName", response.data.Name); // Save schoolName to localStorage
        } catch (error) {
          console.error("Error fetching school data:", error);
        } finally {
          setLoadingSchool(false);
        }
      }
    };

    fetchSchoolData();
  }, [schoolId]);

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(e.target.value);
  };

  const [selectedYear, setSelectedYear] = useState<string>("");

  useEffect(() => {
    if (academicYears.length === 0) return;

    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;

    const startYear = currentMonth >= 8 ? currentYear : currentYear - 1;
    const endYear = startYear + 1;

    const currentEduYear = `${startYear}-${endYear}`;

    if (academicYears.includes(currentEduYear)) {
      setSelectedYear(currentEduYear);
    } else {
      setSelectedYear(academicYears[0]);
    }
  }, [academicYears]);
  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        let response;

        if (schoolId === 9999) {
          response = await axios.get(
            "https://eyelexia.eyesoft.com.tr/api/api/academicyears"
          );
        } else {
          response = await axios.get(
            "https://eyelexia.eyesoft.com.tr/api/api/academicyears",
            {
              params: {
                type: "school",
                school_id: schoolId,
              },
            }
          );
        }

        const years = response.data.map(
          (item: { academic_year: string | number }) => {
            const start = item.academic_year.toString();
            const end = (parseInt(start) + 1).toString();
            return `${start}-${end}`;
          }
        );
        setAcademicYears(years);
      } catch (error) {
        console.error("Akademik yıllar alınamadı:", error);
      }
    };

    fetchAcademicYears();
  }, [schoolId]);

  useEffect(() => {
    const fetchStudentsData = async () => {
      if (!schoolId || !selectedYear) return;

      const [startYearStr, endYearStr] = selectedYear
        .split("-")
        .map((s) => s.trim());

      const startDate = `${startYearStr}-08-01`;
      const endDate = `${endYearStr}-07-31`;
      setLoading(true);

      // Bugünün tarihine göre eğitim yılı hesapla
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1; // 0-indexed

      // İçinde bulunduğumuz eğitim yılına denk gelen başlangıç yılı
      const currentEduStartYear =
        currentMonth >= 8 ? currentYear : currentYear - 1;
      const isCurrentEducationYear =
        parseInt(startYearStr) === currentEduStartYear;

      try {
        let response;
        if (isCurrentEducationYear) {
          // Güncel eğitim yılıysa
          if (schoolId === 9999) {
            response = await axios.get(
              `https://eyelexia.eyesoft.com.tr/api/api/sessions?start_date=${startDate}&end_date=${endDate}&type=all_or_none` //54.93.173.54
            );
          } else {
            response = await axios.get(
              `https://eyelexia.eyesoft.com.tr/api/api/sessions?start_date=${startDate}&end_date=${endDate}&school_id=${schoolId}&type=with_or_none`
            );
          }
        } else {
          // Geçmiş eğitim yılıysa
          if (schoolId === 9999) {
            response = await axios.get(
              `https://eyelexia.eyesoft.com.tr/api/api/sessions?start_date=${startDate}&end_date=${endDate}&type=all`
            );
          } else {
            response = await axios.get(
              `https://eyelexia.eyesoft.com.tr/api/api/sessions?start_date=${startDate}&end_date=${endDate}&school_id=${schoolId}&type=with`
            );
          }
        }
        if (Array.isArray(response.data) && response.data.length === 0) {
          setNoData(true);
          setStudents([]);
        } else if (Array.isArray(response.data)) {
          // Sadece 2. ve 3. sınıfları göster
          const filtered = response.data.filter(
            (student) =>
              ["2", "3", 2, 3].includes(student.session_class) ||
              (student.session_class === null && student.student_class < 4)
          );
          setNoData(filtered.length === 0);
          setStudents(filtered);
          localStorage.setItem("students", JSON.stringify(filtered));
        } else {
          setNoData(true);
          setStudents([]); // güvenli boş dizi
        }
        localStorage.setItem("students", JSON.stringify(response.data));
      } catch (error) {
        console.error("Error fetching students data:", error);
      } finally {
        setLoading(false);
      }
    };

    localStorage.removeItem("students");
    fetchStudentsData();
  }, [schoolId, selectedYear]);

  useEffect(() => {
    const storedSchoolName = localStorage.getItem("schoolName");
    if (storedSchoolName) {
      setSchoolName(storedSchoolName);
    }
  }, []);

  useEffect(() => {
    const storedStudents = localStorage.getItem("students");
    if (storedStudents) {
      setStudents(JSON.parse(storedStudents));
      setLoading(false);
    }
  }, []);

  const getSessionStatusText = (status: string) => {
    switch (status) {
      case "1":
        return `${intl.formatMessage({
          id: "dyslexia",
        })}`;
      default:
        return `${intl.formatMessage({
          id: "incorrectsession",
        })}`;
    }
  };

  const formattedData = useMemo(
    () =>
      students.map((student) => ({
        id: student.ID,
        sessionstatus: getSessionStatusText(
          student.SessionStatus || student.session_status
        ),
        recordDateTime: dayjs(student.recordDateTime).format(
          "YYYY-MM-DD HH:mm:ss"
        ),
        ...student,
      })),
    [students]
  );

  const [filteredRows, setFilteredRows] = useState(formattedData);

  useEffect(() => {
    const searchRegex = new RegExp(searchQuery, "i");

    const filtered = formattedData.filter((row) => {
      const matchSearch = searchRegex.test(row.student_name);

      if (!selectedYear) return matchSearch;

      const [startYearStr, endYearStr] = selectedYear
        .split("-")
        .map((s) => s.trim());

      const startDate = `${startYearStr}-08-01`;
      const endDate = `${endYearStr}-07-31`;

      const recordDate = dayjs(row.record_datetime);
      const isInRange =
        recordDate.isValid() &&
        recordDate.isBetween(startDate, endDate, null, "[]");

      const isMissingScan =
        (!recordDate.isValid() || !row.record_datetime) &&
        dayjs(row.RecordDate).isBetween(startDate, endDate, null, "[]");

      return matchSearch && (isInRange || isMissingScan);
    });

    setFilteredRows(filtered);
  }, [searchQuery, selectedYear, formattedData]);

  const getGenderText = (Gender: string) => {
    switch (Gender) {
      case "0":
        return `${intl.formatMessage({
          id: "man",
        })}`;
      case "1":
        return `${intl.formatMessage({
          id: "woman",
        })}`;
      default:
        return `${intl.formatMessage({
          id: "unknown",
        })}`;
    }
  };
  const downloadAllSessionsAsXlsx = async () => {
    const data = students.map((session) => {
      try {
        const recordDate = dayjs(session.record_datetime);
        return {
          [intl.formatMessage({ id: "name" })]: session.student_name,
          [intl.formatMessage({ id: "class" })]: session.student_class,
          [intl.formatMessage({ id: "branch" })]: session.student_branch,
          [intl.formatMessage({ id: "gender" })]: getGenderText(session.gender),
          [intl.formatMessage({ id: "birthday" })]: session.birthdate,
          [intl.formatMessage({ id: "date" })]: recordDate.isValid()
            ? recordDate.format("YYYY-MM-DD HH:mm:ss")
            : intl.formatMessage({ id: "dontsession" }),
          [intl.formatMessage({ id: "statusclass" })]: session.session_class,
          [intl.formatMessage({ id: "dyslexia%" })]:
            session.session_type === 2 ||
            session.session_type === 3 ||
            session.dyslexia_possibility === 0 ||
            session.dyslexia_possibility == null ||
            session.dyslexia_possibility == undefined ||
            isNaN(session.dyslexia_possibility)
              ? "-"
              : getDyslexiaLevelText(session.dyslexia_possibility),
          [intl.formatMessage({ id: "note" })]: session.session_note,
          ...(schoolId === 9999 && {
            [intl.formatMessage({ id: "province" })]: session.province,
            [intl.formatMessage({ id: "district" })]: session.district,
            [intl.formatMessage({ id: "schoolname" })]: session.school_name,
          }),
        };
      } catch (err) {
        console.error("Error processing session:", err);
        return null;
      }
    });

    // Verileri filtrele (null olanları kaldır)
    const filteredData = data.filter((row) => row !== null);

    try {
      // Veriyi çalışma kitabına dönüştür
      const worksheet = XLSX.utils.json_to_sheet(filteredData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sessions");

      // Bugünün tarihini dosya adına ekle
      const today = new Date();
      const formattedDate = today.toISOString().slice(0, 10);
      const fileName = `${intl.formatMessage({
        id: "filename",
      })}_${formattedDate}.xlsx`;

      // Dosyayı indir
      XLSX.writeFile(workbook, fileName);

      alert(intl.formatMessage({ id: "successalert" }));
    } catch (error) {
      console.error("Error generating XLSX:", error);
    }
  };
  const handleClear = () => {
    setSearchText("");
    setSearchQuery("");
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };
  const handleSearchClick = () => {
    setSearchQuery(searchText);
  };

  const handleOpen = (student: {
    student_id: number;
    student_name: string;
    session_class: string;
    session_branch: string;
    school_name: string;
    dyslexia_possibility: number;
    session_type: number;
    tc: string;
    province: string;
    district: string;
  }) => {
    setSelectedStudent(student);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedStudent(null);
  };
  const columns: GridColDef[] = [
    {
      field: "student_name",
      headerName: `${intl.formatMessage({
        id: "name",
      })}`,
      flex: 2,
    },
    {
      field: "tc",
      headerName: `${intl.formatMessage({
        id: "tc",
      })}`,
      flex: 1.5,
    },
    {
      field: "session_class",
      headerName: `${intl.formatMessage({
        id: "class",
      })}`,
      flex: 0.7,
      renderCell: (params) => {
        const value =
          params.row.session_class !== null &&
          params.row.session_class !== undefined &&
          params.row.session_class !== ""
            ? params.row.session_class
            : params.row.student_class ?? "-";

        return (
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            {value}
          </Typography>
        );
      },
    },
    {
      field: "session_branch",
      headerName: `${intl.formatMessage({
        id: "branch",
      })}`,
      flex: 0.7,
      renderCell: (params) => {
        const value =
          params.row.session_branch !== null &&
          params.row.session_branch !== undefined &&
          params.row.session_branch !== ""
            ? params.row.session_branch
            : params.row.student_branch ?? "-";

        return (
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            {value}
          </Typography>
        );
      },
    },
    ...(schoolId === 9999
      ? [
          {
            field: "school_name",
            headerName: `${intl.formatMessage({
              id: "schoolname",
            })}`,
            flex: 3,
          },
          {
            field: "province",
            headerName: `${intl.formatMessage({
              id: "province",
            })}`,
            flex: 1.5,
          },
          {
            field: "district",
            headerName: `${intl.formatMessage({
              id: "district",
            })}`,
            flex: 1.5,
          },
        ]
      : []),
    {
      field: "dyslexia_possibility",
      headerName: `${intl.formatMessage({ id: "dyslexia%" })}`,
      flex: 1.2,
      renderCell: (params) => {
        const sessionType = params.row.SessionType;
        const value = params.value;
        // Eğer sessionType 2/3 ise, ya da değer null/undefined/NaN ya da 0 ise, "-"
        if (
          sessionType === 2 ||
          sessionType === 3 ||
          value === null ||
          value === undefined ||
          isNaN(value) ||
          value === 0
        ) {
          return (
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              -
            </Typography>
          );
        }
        return (
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            {getDyslexiaLevelText(value)}
          </Typography>
        );
      },
    },
    {
      field: "record_datetime",
      headerName: `${intl.formatMessage({
        id: "currentdiagnosisdate",
      })}`,
      flex: 2,
      renderCell: (params) => {
        const recordDate = dayjs(params.value);

        return (
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              color: recordDate.isValid() ? "inherit" : "red", // Rengi kırmızı yapıyoruz
            }}
          >
            {recordDate.isValid()
              ? recordDate.format("YYYY/MM/DD HH:mm")
              : `${intl.formatMessage({
                  id: "dontsession",
                })}`}
          </Typography>
        );
      },
    },
    {
      field: "button",
      headerName: `${intl.formatMessage({
        id: "showinformation",
      })}`,
      renderCell: (params: any) => (
        <strong>
          <Button
            onClick={() => handleOpen(params.row)}
            variant="contained"
            color="primary"
            size="small"
            sx={{
              fontSize: "0.8rem",
            }}
            style={{ marginLeft: 16 }}
          >
            {intl.formatMessage({
              id: "detail",
            })}
          </Button>
        </strong>
      ),
      flex: 1.5,
    },
  ];

  return (
    <IntlProvider
      locale={language}
      messages={messages[language] as unknown as Record<string, string>}
    >
      <MiniDrawer userName={schoolName}>
        <ThemeProvider theme={theme}>
          <Container
            component="main"
            maxWidth="xl"
            sx={{ width: "100%", height: "87vh" }}
          >
            <CssBaseline />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap", // Küçük ekranlarda taşma olursa alta geçmesi için
                  gap: 2,
                  mb: 2,
                }}
              >
                {/* Başlık */}
                <Typography
                  sx={{
                    fontSize: "2rem",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                  component="div"
                >
                  {intl.formatMessage({ id: "studentlist" })}
                </Typography>

                {/* Arama kutusu + buton */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flex: 1,
                    minWidth: 300,
                    maxWidth: 600,
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={intl.formatMessage({ id: "studentname" })}
                    value={searchText}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSearchClick(); // Enter'a basınca arama
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchText && (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClear} edge="end">
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 40,
                        "& fieldset": { borderColor: "#A6A6A6" },
                        "&:hover fieldset": { borderColor: "#868686" },
                        "&.Mui-focused fieldset": { borderColor: "#56828C" },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearchClick}
                    sx={{
                      height: 40,
                      minWidth: 90,
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {intl.formatMessage({ id: "search" })}
                  </Button>
                </Box>

                {/* Excel butonu */}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={downloadAllSessionsAsXlsx}
                  sx={{
                    height: 40,
                    minWidth: 180,
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {intl.formatMessage({ id: "allstudentdownload" })}
                </Button>

                {/* Yıl seçici */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ whiteSpace: "nowrap" }}>
                    {intl.formatMessage({ id: "selectAcademicYear" })}:
                  </Typography>
                  <select
                    value={selectedYear || ""}
                    onChange={handleYearChange}
                    style={{
                      padding: "8px",
                      fontSize: "1rem",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                    }}
                  >
                    {academicYears.map((year) => {
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <DataGrid
                  getRowClassName={(params) =>
                    !params.row.RecordDateTime ? "row-null-date" : ""
                  }
                  sx={{
                    width: "100%",
                    height: "100%",
                    bgcolor: "#FAFAFA",
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                      backgroundColor: "#f5f5f5",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-cell": {
                      fontSize: "1rem",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                    },
                    "& .MuiTablePagination-root": {
                      fontSize: "1rem",
                    },
                  }}
                  rows={filteredRows}
                  columns={columns}
                  getRowId={(row) => row.student_id}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 50,
                      },
                    },
                  }}
                  pageSizeOptions={[50]}
                  disableRowSelectionOnClick
                  autoHeight={false}
                  loading={loading}
                  localeText={
                    trTR.components.MuiDataGrid.defaultProps.localeText
                  }
                />
              </Box>
            </Box>
          </Container>
        </ThemeProvider>
        {selectedStudent && (
          <StudentInfoModal
            open={open}
            handleClose={handleClose}
            ID={selectedStudent.student_id}
            SchoolName={selectedStudent?.school_name || " "}
            ProvinceName={selectedStudent?.province}
            DistrictName={selectedStudent?.district}
          />
        )}
      </MiniDrawer>
    </IntlProvider>
  );
};
export default SchoolReportList;
